import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation } from "wouter";
import { Header } from "@/components/Header";
import { ProgressIndicator } from "@/components/ProgressIndicator";
import { SmartAIChat } from "@/components/SmartAIChat";
import { DifficultyLevelInfo } from "@/components/DifficultyDefinitions";
import { StandardsPicker } from "@/components/StandardsPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AutoGrowTextarea } from "@/components/AutoGrowTextarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Plus, Trash2, ChevronDown, ChevronUp, RefreshCw, HelpCircle, AlertTriangle, Info, Check } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useTest } from "@/context/TestContext";
import { useDevMode } from "@/context/DevModeContext";
import { useAuth } from "@/context/AuthContext";
import { readingLevelToGrade, type Blueprint, type BlueprintTopic, type Standard, type ContentSegment, type SourceAnchor } from "@shared/schema";
import { formatSourceAnchors } from "@/utils/sourceAnchors";
import { SOURCE_BANNER_KEY } from "@/pages/IdentifyContent";
import { useChatHalfHeight } from "@/hooks/use-chat-half-height";
import { useChatNarrowWidth } from "@/hooks/use-chat-narrow-width";

// Mothballed for MVP: hides the per-topic Standards picker + per-standard
// distribution table on the Build Blueprint screen. Flip to true to restore
// the standards UI (all the supporting logic is kept intact).
const STANDARDS_ENABLED = false;

// Hard cap on the number of items a single test may contain. Any item-count
// edit is clamped so the total across all topics never exceeds this.
const MAX_TEST_ITEMS = 20;

// First-time onboarding banner on the Build Blueprint page. Once dismissed we
// remember it so it doesn't show again.
const BLUEPRINT_ONBOARDING_KEY = "testcraft.blueprintOnboardingSeen";

// Item-count inputs fill their whole table cell with no border/background, so
// the table's own grid line becomes the field's box. Absolute inset means the
// field matches the cell height even when the Topic name wraps to extra lines.
const ITEM_INPUT_CLASS =
  "absolute inset-0 w-full h-full border-0 rounded-none bg-transparent text-center pl-2 pr-8 " +
  "text-2xl md:text-2xl font-semibold " + // override the base Input's md:text-sm
  // Hide the native spinner — we render large custom up/down buttons instead.
  "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none " +
  "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring focus-visible:ring-offset-0";

// ============================================================================
// Blueprint allocation math (Blueprint Calculation Logic v1.2)
// ============================================================================

function countWords(text: string): number {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
}

interface AllocInput {
  name: string;
  wordCount: number; // document order is the array index
}

// §4.4 Item allocation across topics. Honors the floor of 1 always; meets T
// as closely as rounding allows; only exceeds T when more topics than items.
function allocateItems(topics: AllocInput[], T: number): number[] {
  if (topics.length === 0) return [];
  const target = Math.max(1, T);
  const totalWords = topics.reduce((s, t) => s + t.wordCount, 0);

  const weights = totalWords > 0
    ? topics.map((t) => t.wordCount / totalWords)
    : topics.map(() => 1 / topics.length); // fall back to equal weighting

  const raws = weights.map((w) => target * w);
  const initial = raws.map((r) => Math.floor(r));
  const remainders = raws.map((r, i) => r - initial[i]);
  const counts = initial.map((c) => Math.max(1, c)); // floor of 1

  // Track which topics got bumped from <1 → 1 (the "floor cause" for any overage)
  const floorBumped = initial.map((c) => c < 1);

  let sum = counts.reduce((s, c) => s + c, 0);

  // §4.4 step 4: if sum < T, add toward T, largest fractional remainder wins.
  // Tie-break: larger word count, then earlier document order.
  if (sum < target) {
    const order = topics
      .map((_, i) => i)
      .sort((a, b) => {
        if (remainders[a] !== remainders[b]) return remainders[b] - remainders[a];
        if (topics[a].wordCount !== topics[b].wordCount) return topics[b].wordCount - topics[a].wordCount;
        return a - b; // earlier document order
      });
    let cursor = 0;
    while (sum < target) {
      counts[order[cursor % order.length]]++;
      sum++;
      cursor++;
    }
  } else if (sum > target) {
    // §4.4 step 5 vs §4.4 step 6: did the floor cause this overage, or
    // ordinary rounding? If any topic was bumped from <1 to 1 and that's
    // pushing us over, it's the floor case — allow the overflow.
    const bumps = floorBumped.filter(Boolean).length;
    const initialSum = initial.reduce((s, c) => s + c, 0);
    const headroom = target - initialSum; // bumps the floor can absorb without overflow
    const floorCaused = bumps > headroom;

    if (!floorCaused) {
      // §4.4 step 5: remove from topics with count > 1.
      // Tie-break: smallest fractional remainder, then smaller word count,
      // then later document order.
      const order = topics
        .map((_, i) => i)
        .sort((a, b) => {
          if (remainders[a] !== remainders[b]) return remainders[a] - remainders[b];
          if (topics[a].wordCount !== topics[b].wordCount) return topics[a].wordCount - topics[b].wordCount;
          return b - a; // later document order
        });
      let cursor = 0;
      let safety = order.length * 10; // can't loop forever
      while (sum > target && safety-- > 0) {
        const idx = order[cursor % order.length];
        if (counts[idx] > 1) {
          counts[idx]--;
          sum--;
        }
        cursor++;
      }
    }
    // else: §4.4 step 6 — leave alone, total intentionally exceeds T.
  }

  return counts;
}

// §4.5 Level split within a topic. Even split, remainders go LOW first
// (K before U before H — never to H).
function splitLevels(m: number): { knowledge: number; understanding: number; higherOrderThinking: number } {
  const safe = Math.max(0, Math.floor(m));
  const base = Math.floor(safe / 3);
  const remainder = safe % 3;
  return {
    knowledge: base + (remainder >= 1 ? 1 : 0),
    understanding: base + (remainder >= 2 ? 1 : 0),
    higherOrderThinking: base,
  };
}

// ============================================================================
// Source-anchor formatting for the Content Alignment tooltip lives in
// @/utils/sourceAnchors (shared with the Teacher Copy export).
// ============================================================================

// ============================================================================

// Numeric item-count input. Keeps a local text buffer so the field can be
// cleared while editing (letting you delete a leading 0 instead of typing
// "013"), and coerces an empty field back to 0 on blur. Stays in sync when
// the parent clamps the committed value (e.g. the 20-item cap) or when the
// value is driven externally (per-standard reconciliation, regenerate).
function ItemCountInput({
  value,
  onCommit,
  className,
  disabled,
  readOnly,
  title,
  stepper,
  "data-testid": testId,
}: {
  value: number;
  onCommit: (n: number) => number; // returns the actually-applied (clamped) value
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  title?: string;
  // When true, render large custom up/down buttons (native spinner hidden via
  // the className) instead of the browser's tiny number spinner.
  stepper?: boolean;
  "data-testid"?: string;
}) {
  const [raw, setRaw] = useState(String(value));
  const focused = useRef(false);

  useEffect(() => {
    // Mirror the external value when we're not actively editing (regenerate,
    // per-standard reconciliation, a clamp triggered by another box, etc.).
    // While editing, onChange already reflects the applied value.
    if (!focused.current) setRaw(String(value));
  }, [value]);

  // Step the committed value by ±1; onCommit clamps and returns the applied
  // value, which we mirror into the field.
  const step = (delta: number) => {
    const applied = onCommit(Math.max(0, value + delta));
    setRaw(String(applied));
  };

  const input = (
    <Input
      type="number"
      min="0"
      value={raw}
      onFocus={() => { focused.current = true; }}
      onChange={(e) => {
        let next = e.target.value;
        if (next === "") {
          setRaw("0"); // never leave the box blank — show 0 the instant it empties
          onCommit(0);
          return;
        }
        // A lone leading 0 is dropped as soon as a digit is typed (0 -> 5, not 05).
        if (next.length > 1 && next.startsWith("0")) {
          next = next.replace(/^0+/, "") || "0";
        }
        const typed = Number(next);
        const applied = onCommit(typed);
        // If the parent clamped the value, show the clamp; otherwise keep the
        // raw text (so e.g. a trailing "013" survives until blur normalizes it).
        setRaw(applied === typed ? next : String(applied));
      }}
      onBlur={() => {
        focused.current = false;
        if (raw === "" || Number.isNaN(Number(raw))) {
          setRaw("0");
          onCommit(0);
        } else {
          setRaw(String(Number(raw))); // normalize e.g. "013" -> "13"
        }
      }}
      disabled={disabled}
      readOnly={readOnly}
      title={title}
      className={className}
      data-testid={testId}
    />
  );

  if (!stepper) return input;

  const stepBtn =
    "flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:pointer-events-none";

  return (
    <>
      {input}
      <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex flex-col gap-1 z-10">
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled || readOnly}
          onClick={() => step(1)}
          aria-label="Increase items"
          className={stepBtn}
          data-testid={testId ? `${testId}-up` : undefined}
        >
          <ChevronUp className="w-6 h-6" />
        </button>
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled || readOnly}
          onClick={() => step(-1)}
          aria-label="Decrease items"
          className={stepBtn}
          data-testid={testId ? `${testId}-down` : undefined}
        >
          <ChevronDown className="w-6 h-6" />
        </button>
      </div>
    </>
  );
}

export default function BuildBlueprint() {
  const [, setLocation] = useLocation();
  const { config, blueprint, setBlueprint } = useTest();
  const { token } = useAuth();
  const { devMode } = useDevMode();
  const [generatedBlueprint, setGeneratedBlueprint] = useState<Blueprint | null>(blueprint || null);
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  // Set when blueprint generation fails. We surface a clear error + retry
  // instead of masking the failure with placeholder topics.
  const [generationError, setGenerationError] = useState<string | null>(null);
  // Bumped by "Try again" so generation re-runs even when generatedBlueprint
  // is already null (the error state).
  const [retryNonce, setRetryNonce] = useState(0);
  // First-time-only onboarding banner explaining the blueprint table.
  const [showOnboarding, setShowOnboarding] = useState<boolean>(() => {
    try {
      return typeof window !== "undefined" && localStorage.getItem(BLUEPRINT_ONBOARDING_KEY) !== "1";
    } catch {
      return false;
    }
  });
  // "Got it" persists the dismissal (so the banner won't show on future
  // blueprints) but keeps it on screen for this visit, swapping the button for
  // a confirmation note.
  const [onboardingConfirmed, setOnboardingConfirmed] = useState(false);
  // Desktop sidebar assistant: chevron toggles between full-viewport and
  // half-viewport height. Shared store so toggling persists across page
  // navigations (Build Blueprint <-> Review Blueprint, etc.).
  const [assistantHalfHeight] = useChatHalfHeight();
  const [assistantNarrow] = useChatNarrowWidth();
  const dismissOnboarding = () => {
    try {
      localStorage.setItem(BLUEPRINT_ONBOARDING_KEY, "1");
    } catch {
      /* private mode */
    }
    setOnboardingConfirmed(true);
  };

  // The "New here?" banner. Rendered above the table AND above the generating
  // spinner, so first-time users can read it while the blueprint loads.
  const onboardingBanner = showOnboarding ? (
    <div className="rounded-lg border border-secondary/30 bg-secondary/5 p-4" data-testid="blueprint-onboarding">
      <div className="flex gap-3">
        <Info className="w-5 h-5 shrink-0 mt-0.5 text-secondary" />
        <div className="space-y-1.5 text-sm text-foreground/90">
          <p className="font-semibold text-foreground">New here? How to read your blueprint:</p>
          <p>
            A blueprint is the plan we'll use to generate your test — the topics it covers, how many
            questions each has, and at what cognitive level. Review it before moving on.
          </p>
          <p>
            The numbers beneath <span className="font-medium">Knowledge</span>,{" "}
            <span className="font-medium">Understanding</span>, and{" "}
            <span className="font-medium">Application+</span> are how many test questions each
            topic gets at that cognitive level. Adjust them with the arrows or by selecting a box and typing.
          </p>
          <p>
            <span className="font-medium">Topic Weight</span> is each topic's share of the whole test —
            its percentage and total item count, updated as you change the boxes.
          </p>
          <p className="text-muted-foreground">
            Heads up: this blueprint is based on content emphasis in your uploaded material. It doesn't
            infer what you taught, what students need most, or what your objectives require — please
            adjust before generating items.
          </p>
          <div className="pt-1.5">
            {onboardingConfirmed ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="blueprint-onboarding-confirmed">
                <Check className="w-4 h-4 text-success shrink-0" />
                You won't see this in the future.
              </div>
            ) : (
              <Button size="sm" variant="secondary" onClick={dismissOnboarding} data-testid="button-got-it-blueprint-onboarding">
                Got it
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  // Requested test length, clamped to the hard cap so a generated blueprint
  // can never target more than MAX_TEST_ITEMS items.
  const totalItems = Math.min(config?.testLengthValue || 25, MAX_TEST_ITEMS);

  // Auto-generate blueprint on mount if not already generated
  useEffect(() => {
    if (!generatedBlueprint && config) {
      const fetchBlueprint = async () => {
        setGenerationError(null);
        try {
          // Assemble the materials text we send to the blueprint AI, tagging
          // each segment from each uploaded file with a stable [[sN]] marker.
          // The AI is instructed to preserve those markers inside the
          // contentText it returns per topic; we then parse them back here
          // to derive source anchors for the Content Alignment tooltip.
          type MaterialLike = { name?: string; content?: string; url?: string; segments?: ContentSegment[] };
          const allSources: MaterialLike[] = [];
          for (const m of (config.materials ?? [])) allSources.push(m as MaterialLike);

          const anchorById = new Map<string, { anchor: SourceAnchor; sourceName: string }>();
          const sectionTexts: string[] = [];
          let nextSegmentNumber = 1;

          for (const src of allSources) {
            const name = src.name || 'Material';
            const segments: ContentSegment[] = Array.isArray(src.segments) ? src.segments : [];
            if (segments.length > 0) {
              const lines: string[] = [`Source: ${name}`];
              for (const seg of segments) {
                if (!seg.text) continue;
                const id = `s${nextSegmentNumber++}`;
                if (seg.anchor) anchorById.set(id, { anchor: seg.anchor, sourceName: name });
                lines.push(`[[${id}]] ${seg.text}`);
              }
              sectionTexts.push(lines.join('\n\n'));
            } else if (src.content) {
              // No segments (legacy upload, text input, URL): include the
              // content without a marker — this content won't be alignable.
              sectionTexts.push(`Source: ${name}\n\n${src.content}`);
            }
          }

          // Additional Context is teacher-provided direction, NOT source
          // material. Tag it clearly so Claude treats it as instructions
          // (what to emphasize, pacing, tone, accommodations) rather than
          // as content to pull questions from.
          const extraContext = (config.additionalContext ?? '').trim();
          if (extraContext) {
            sectionTexts.push(
              `=== ADDITIONAL CONTEXT FROM TEACHER ===\nThe following are instructions and constraints from the teacher about how to shape the test. They are NOT source material — do not generate questions directly from this text. Use it to guide what you emphasize, the level of difficulty, and any pacing or coverage notes.\n\n${extraContext}\n=== END ADDITIONAL CONTEXT ===`,
            );
          }

          const allText = sectionTexts.filter(Boolean).join('\n\n---\n\n');

          const gradeNum = readingLevelToGrade(config.readingLevel);

          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };

          // Add Authorization header if token exists
          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          }

          const response = await fetch("/api/blueprint/generate", {
            method: "POST",
            headers,
            body: JSON.stringify({
              materialsText: allText || `Create a test for ${config.audience}`,
              audience: config.audience,
              grade: gradeNum,
              readingLevel: config.readingLevel,
              subject: config.subject,
              totalItems,
            }),
          });

          if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            const base = errorBody.error || `HTTP ${response.status}`;
            throw new Error(errorBody.details ? `${base}: ${errorBody.details}` : base);
          }
          const data = await response.json();

          // Blueprint Calculation Logic v1.2:
          // 1. AI returns topic name + contentText (the source material excerpt
          //    that pertains to it). We count words in those segments ourselves
          //    (deterministic, no token waste).
          // 2. Word counts drive topic weights (§4.3) and allocation (§4.4).
          // 3. Each topic's item count splits evenly across K/U/H per §4.5.
          // 4. Reconciliation runs ONCE here. User edits after this are
          //    preserved untouched (§5.1).
          type RawTopic = { id?: string; name: string; contentText?: string };
          const rawTopics: RawTopic[] = Array.isArray(data.topics) ? data.topics : [];
          const allocInputs: AllocInput[] = rawTopics.map((t) => ({
            name: t.name,
            wordCount: countWords(t.contentText || ""),
          }));
          const totalWords = allocInputs.reduce((s, t) => s + t.wordCount, 0);
          const itemCounts = allocateItems(allocInputs, totalItems);

          // For each topic, pull [[sN]] markers out of its contentText and
          // resolve them to source anchors via the anchorById map built
          // before the request. Anchors are deduped per topic: e.g. if a
          // topic cites pages 3, 4, and 5 of the same file, we end up with
          // one pdf-page anchor per page (the UI renders them as a range).
          const resolveAnchors = (text: string): SourceAnchor[] => {
            const ids = new Set<string>();
            const re = /\[\[(s\d+)\]\]/g;
            let m: RegExpExecArray | null;
            while ((m = re.exec(text)) !== null) ids.add(m[1]);
            const out: SourceAnchor[] = [];
            const seen = new Set<string>();
            Array.from(ids).forEach((id) => {
              const hit = anchorById.get(id);
              if (!hit) return;
              // Dedup by anchor identity (file + page/slide/heading).
              const key = JSON.stringify([hit.sourceName, hit.anchor]);
              if (seen.has(key)) return;
              seen.add(key);
              // Stamp the filename onto the anchor so the tooltip can show
              // "Drawn from pages 3-5 of textbook.pdf".
              out.push({ ...hit.anchor, source: hit.sourceName } as SourceAnchor);
            });
            return out;
          };

          const topics: BlueprintTopic[] = rawTopics.map((t, i) => {
            const items = itemCounts[i] ?? 0;
            const levels = splitLevels(items);
            const weight = totalWords > 0 ? allocInputs[i].wordCount / totalWords : 1 / rawTopics.length;
            const sourceAnchors = resolveAnchors(t.contentText || '');
            return {
              id: t.id || `topic-${Date.now()}-${i}`,
              name: t.name,
              percentage: Math.round(weight * 100),
              ...levels,
              ...(sourceAnchors.length > 0 ? { sourceAnchors } : {}),
            };
          });
          const newBlueprint = { topics };
          setGeneratedBlueprint(newBlueprint);
          setBlueprint(newBlueprint);
        } catch (error) {
          console.error("Failed to generate blueprint:", error);
          // Surface the failure clearly rather than masking it with placeholder
          // topics — the teacher should know nothing usable came back.
          setGenerationError(error instanceof Error ? error.message : "Unknown error");
        }
      };

      fetchBlueprint();
    }
  }, [generatedBlueprint, config, totalItems, setBlueprint, retryNonce]);

  const handleUpdateTopic = (topicId: string, field: keyof BlueprintTopic, value: string | number): string | number | void => {
    if (!generatedBlueprint) return;

    // Clamp item-count edits so the test total never exceeds MAX_TEST_ITEMS.
    let nextValue = value;
    if (
      typeof value === "number" &&
      (field === "knowledge" || field === "understanding" || field === "higherOrderThinking")
    ) {
      const topic = generatedBlueprint.topics.find((t) => t.id === topicId);
      const oldValue = topic ? (topic[field] as number) : 0;
      const totalOthers = calculateTotalItems() - oldValue;
      const maxAllowed = Math.max(0, MAX_TEST_ITEMS - totalOthers);
      nextValue = Math.max(0, Math.min(value, maxAllowed));
    }

    const updatedTopics = generatedBlueprint.topics.map((topic) =>
      topic.id === topicId ? { ...topic, [field]: nextValue } : topic
    );

    const updated = { ...generatedBlueprint, topics: updatedTopics };
    setGeneratedBlueprint(updated);
    setBlueprint(updated); // Sync to context for localStorage auto-save
    return nextValue; // the actually-applied (clamped) value
  };

  // ---------------------------------------------------------------------------
  // Standards (per-topic, per-standard K/U/HOT distribution). MOTHBALLED for
  // MVP: the picker UI is gated off via STANDARDS_ENABLED, but the logic is
  // kept intact so the feature can be flipped back on later.
  // ---------------------------------------------------------------------------
  const updateTopic = (topicId: string, updatedTopic: BlueprintTopic) => {
    if (!generatedBlueprint) return;

    const updatedTopics = generatedBlueprint.topics.map((topic) =>
      topic.id === topicId ? updatedTopic : topic
    );

    const updated = { ...generatedBlueprint, topics: updatedTopics };
    setGeneratedBlueprint(updated);
    setBlueprint(updated); // Sync to context for localStorage auto-save
  };

  // When a topic has standards with explicit K/U/HOT counts, the topic's
  // own totals are the sum of those counts (single source of truth).
  // Returns the topic with totals recomputed from its standards.
  const reconcileTopicTotalsFromStandards = (topic: BlueprintTopic): BlueprintTopic => {
    const standards = topic.standards || [];
    if (standards.length === 0) return topic;
    return {
      ...topic,
      knowledge: standards.reduce((sum, s) => sum + (s.knowledge || 0), 0),
      understanding: standards.reduce((sum, s) => sum + (s.understanding || 0), 0),
      higherOrderThinking: standards.reduce((sum, s) => sum + (s.higherOrderThinking || 0), 0),
    };
  };

  // Update one standard on a topic; reconcile topic totals.
  const updateStandardCount = (
    topicId: string,
    standardCode: string,
    field: 'knowledge' | 'understanding' | 'higherOrderThinking',
    value: number
  ): number | void => {
    if (!generatedBlueprint) return;
    const topic = generatedBlueprint.topics.find(t => t.id === topicId);
    if (!topic || !topic.standards) return;

    // Clamp so the test total never exceeds MAX_TEST_ITEMS.
    const oldValue = topic.standards.find(s => s.code === standardCode)?.[field] || 0;
    const totalOthers = calculateTotalItems() - oldValue;
    const maxAllowed = Math.max(0, MAX_TEST_ITEMS - totalOthers);
    const nextValue = Math.max(0, Math.min(value, maxAllowed));

    const nextStandards = topic.standards.map(s =>
      s.code === standardCode ? { ...s, [field]: nextValue } : s
    );
    updateTopic(topicId, reconcileTopicTotalsFromStandards({ ...topic, standards: nextStandards }));
    return nextValue; // the actually-applied (clamped) value
  };

  // When the StandardsPicker selection changes, preserve existing per-standard
  // counts and seed brand-new standards from the topic-level counts (so adding
  // the first standard to a topic doesn't wipe its distribution).
  const handleStandardsChange = (topicId: string, nextStandards: Standard[]) => {
    if (!generatedBlueprint) return;
    const topic = generatedBlueprint.topics.find(t => t.id === topicId);
    if (!topic) return;
    const prevByCode = new Map((topic.standards || []).map(s => [s.code, s] as const));
    const wasEmpty = (topic.standards || []).length === 0;
    const reconciled = nextStandards.map((s, i) => {
      const prev = prevByCode.get(s.code);
      if (prev) {
        return { ...s, knowledge: prev.knowledge, understanding: prev.understanding, higherOrderThinking: prev.higherOrderThinking };
      }
      // First standard added to a topic that had topic-level counts: seed it
      // from the topic so the existing distribution is preserved.
      if (wasEmpty && i === 0) {
        return { ...s, knowledge: topic.knowledge, understanding: topic.understanding, higherOrderThinking: topic.higherOrderThinking };
      }
      return { ...s, knowledge: 0, understanding: 0, higherOrderThinking: 0 };
    });
    updateTopic(topicId, reconcileTopicTotalsFromStandards({ ...topic, standards: reconciled }));
  };

  const toggleTopicExpanded = (topicId: string) => {
    setExpandedTopics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(topicId)) {
        newSet.delete(topicId);
      } else {
        newSet.add(topicId);
      }
      return newSet;
    });
  };

  const handleAddTopic = () => {
    if (!generatedBlueprint) return;
    
    const newTopic: BlueprintTopic = {
      id: `topic-${Date.now()}`,
      name: "New Topic",
      percentage: 0,
      knowledge: 0,
      understanding: 0,
      higherOrderThinking: 0,
      addedManually: true,
    };

    const updated = {
      ...generatedBlueprint,
      topics: [...generatedBlueprint.topics, newTopic],
    };
    setGeneratedBlueprint(updated);
    setBlueprint(updated); // Sync to context for localStorage auto-save
  };

  const handleRemoveTopic = (topicId: string) => {
    if (!generatedBlueprint) return;

    const updated = {
      ...generatedBlueprint,
      topics: generatedBlueprint.topics.filter(t => t.id !== topicId),
    };
    setGeneratedBlueprint(updated);
    setBlueprint(updated); // Sync to context for localStorage auto-save
  };

  // Clear the current blueprint so the fetch effect kicks off a fresh
  // generation. Wipes any manual edits the user has made to topics.
  const handleRegenerateBlueprint = () => {
    setGenerationError(null);
    setGeneratedBlueprint(null);
    setBlueprint(null);
    setRetryNonce((n) => n + 1);
  };

  // Blueprint-level validation gates the Next button. Two distinct issues:
  //   • zeroItemTopicIds: topics with 0 items — user needs to add items or
  //     delete the topic. Highlights the whole row.
  //   • unnamedTopicIds: topics that still have the placeholder "New Topic"
  //     name (or whitespace-only) AND already have items. If a topic has
  //     0 items it's already flagged by zeroItemTopicIds; no need to nag
  //     about the name too. Highlights the name field.
  // Re-derives on every render via generatedBlueprint — clears immediately
  // as the user edits.
  const zeroItemTopicIds = new Set<string>();
  const unnamedTopicIds = new Set<string>();
  if (generatedBlueprint) {
    for (const t of generatedBlueprint.topics) {
      const items = t.knowledge + t.understanding + t.higherOrderThinking;
      const trimmedName = (t.name ?? "").trim();
      const isUnnamed = !trimmedName || trimmedName === "New Topic";
      if (items === 0) {
        zeroItemTopicIds.add(t.id);
      } else if (isUnnamed) {
        unnamedTopicIds.add(t.id);
      }
    }
  }
  const hasZeroItems = zeroItemTopicIds.size > 0;
  const hasUnnamed = unnamedTopicIds.size > 0;

  // Validation highlights + the "fix this first" message only appear AFTER
  // the user clicks Next with issues present — silent until then. Resets
  // back to false the moment all issues clear, so re-introducing an issue
  // doesn't re-trigger highlights without another Next attempt.
  const [hasAttemptedNext, setHasAttemptedNext] = useState(false);
  useEffect(() => {
    if (!hasZeroItems && !hasUnnamed) {
      setHasAttemptedNext(false);
    }
  }, [hasZeroItems, hasUnnamed]);

  const calculateTotalItems = () => {
    if (!generatedBlueprint) return 0;
    return generatedBlueprint.topics.reduce(
      (sum, topic) => sum + topic.knowledge + topic.understanding + topic.higherOrderThinking,
      0
    );
  };

  const calculatePercentage = (topic: BlueprintTopic) => {
    const total = calculateTotalItems();
    if (total === 0) return 0;
    const topicTotal = topic.knowledge + topic.understanding + topic.higherOrderThinking;
    return Math.round((topicTotal / total) * 100);
  };

  // Only worth labeling manual topics "Added by you" when other topics show
  // where they were drawn from. With links-only (or no) source material, no
  // topic has anchors, so the label would stand alone confusingly.
  const anyTopicHasSourceAnchors = !!generatedBlueprint?.topics.some(
    (t) => t.sourceAnchors && t.sourceAnchors.length > 0,
  );

  // Snapshot of the current blueprint handed to the AI assistant on each
  // message (the chat is stateless), so it always knows the live topics and
  // counts. "Application+" is the displayed label for higherOrderThinking.
  const blueprintContext = generatedBlueprint
    ? {
        testConfig: config
          ? {
              audience: config.audience,
              readingLevel: config.readingLevel,
              testLengthType: config.testLengthType,
              testLengthValue: config.testLengthValue,
              subject: config.subject,
            }
          : undefined,
        totalItems: calculateTotalItems(),
        topics: generatedBlueprint.topics.map((t) => ({
          name: t.name,
          percentage: calculatePercentage(t),
          total: t.knowledge + t.understanding + t.higherOrderThinking,
          knowledge: t.knowledge,
          understanding: t.understanding,
          applicationPlus: t.higherOrderThinking,
          source: formatSourceAnchors(t.sourceAnchors) || undefined,
          addedManually: t.addedManually || undefined,
        })),
      }
    : null;

  // Plain text of the uploaded materials, sent to the assistant ONLY when the
  // "Suggest a topic" chip is clicked (one-shot) so it can propose uncovered
  // topics. No [[sN]] markers needed here; capped so the call stays bounded.
  const materialsContext = useMemo(() => {
    const sources = config?.materials ?? [];
    const sections: string[] = [];
    for (const m of sources as Array<{ type?: string; name?: string; content?: string; segments?: ContentSegment[] }>) {
      const name = m.name || "Material";
      const segs = Array.isArray(m.segments) ? m.segments : [];
      // For a url, the real page text lives in segments (extracted on add);
      // its `content` is just the link string, so don't fall back to it.
      const body = segs.length > 0
        ? segs.map((s) => s.text).filter(Boolean).join("\n\n")
        : (m.type === "url" ? "" : (m.content || ""));
      if (body.trim()) sections.push(`Source: ${name}\n\n${body}`);
    }
    const joined = sections.join("\n\n---\n\n");
    return joined ? joined.slice(0, 60000) : undefined;
  }, [config?.materials]);

  const handleNext = () => {
    if (!generatedBlueprint) return;
    if (hasZeroItems || hasUnnamed) {
      // Don't navigate — surface the highlights + message so the user can
      // see why. The hasAttemptedNext flag drives the visibility and is
      // auto-cleared by the useEffect above once everything's fixed.
      setHasAttemptedNext(true);
      return;
    }
    // Recalculate percentages before saving
    const updatedTopics = generatedBlueprint.topics.map(topic => ({
      ...topic,
      percentage: calculatePercentage(topic),
    }));

    const finalBlueprint = { ...generatedBlueprint, topics: updatedTopics };
    setBlueprint(finalBlueprint);
    setLocation("/create/review-blueprint");
  };

  const handleBack = () => {
    setLocation("/create/identify");
  };


  return (
    <div className="min-h-screen bg-background">
      {/* Dev-mode onboarding toggles (revealed by the secret Dev options panel). */}
      {devMode && (
        <div className="fixed top-3 right-3 z-60 flex gap-2">
          <button
            type="button"
            onClick={() => {
              try {
                localStorage.removeItem(BLUEPRINT_ONBOARDING_KEY);
                // Also clear the Let's Make Your Test "What are lesson materials?"
                // banner so a dev visit re-runs both onboarding surfaces.
                localStorage.removeItem(SOURCE_BANNER_KEY);
              } catch {
                /* private mode */
              }
              setOnboardingConfirmed(false);
              setShowOnboarding(true);
            }}
            className="rounded-md border border-dashed border-amber-400 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 shadow-xs hover:bg-amber-100"
            data-testid="button-dev-reset-onboarding"
          >
            First-time user (dev)
          </button>
          <button
            type="button"
            onClick={() => {
              try {
                localStorage.setItem(BLUEPRINT_ONBOARDING_KEY, "1");
              } catch {
                /* private mode */
              }
              setOnboardingConfirmed(false);
              setShowOnboarding(false);
            }}
            className="rounded-md border border-dashed border-amber-400 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 shadow-xs hover:bg-amber-100"
            data-testid="button-dev-returning-user"
          >
            Returning user (dev)
          </button>
        </div>
      )}
      <Header pageTitle="Create Test" />

      <main className="mx-auto px-6 py-12 pb-28 xl:pb-12 max-w-[1600px]">
        <ProgressIndicator currentStep={2} />

        <div
          className={`grid grid-cols-1 ${assistantNarrow ? "xl:grid-cols-[minmax(0,1fr)_minmax(260px,320px)]" : "xl:grid-cols-[minmax(0,1fr)_minmax(340px,420px)]"} gap-6`}
        >
          <div>
            <Card className="mb-8">
              <CardHeader>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                  <div className="flex items-start gap-0.5 shrink-0">
                    <CardTitle className="text-3xl">Let's Build Your Test Blueprint</CardTitle>
                    {/* Help icon and disclaimer are folded into the first-time
                        onboarding banner; they reappear here once it's dismissed. */}
                    {!showOnboarding && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label="What is a blueprint?"
                            className="self-start mt-1 text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            <HelpCircle className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm">
                          <p className="font-medium mb-1">What's a blueprint?</p>
                          <p className="text-sm leading-relaxed">
                            A breakdown of your test: which topics it covers, how many questions on each, and the
                            cognitive level (Knowledge / Understanding / Application+) of those questions.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  {!showOnboarding && (
                    <div className="flex-1 rounded-md border-l-4 border-l-primary bg-primary/5 px-4 py-3 text-sm text-foreground/90">
                      This blueprint is based on content emphasis in the uploaded material. It does not infer what you taught, what students need most, or what your objectives require. Please adjust before generating items.
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {generatedBlueprint ? (
                  <div className="space-y-6">
                    {onboardingBanner}

                    {/* Table Header */}
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            <th colSpan={2} className="text-left align-middle px-4 py-2">
                              {/* Regenerate Blueprint — aligned with the Item Distribution header */}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    data-testid="button-regenerate-blueprint"
                                  >
                                    Regenerate Blueprint <RefreshCw className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Regenerate this blueprint?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will discard your current topics, per-standard distributions, and any other edits you've made on this page. The AI will produce a fresh blueprint from your test settings.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={handleRegenerateBlueprint}>
                                      Regenerate
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </th>
                            <th
                              colSpan={3}
                              className="text-center py-2 px-4 bg-muted/30 border border-b-0 border-border"
                            >
                              <div className="text-lg font-semibold text-foreground">Item Distribution</div>
                              <div className="text-sm font-normal text-muted-foreground">
                                Click a level below for its definition
                              </div>
                            </th>
                            <th></th>
                          </tr>
                          <tr className="border-b-2 border-border text-lg">
                            <th className="text-left py-4 px-4 font-semibold text-foreground min-w-[16rem]">
                              Topic
                            </th>
                            <th className="text-left py-4 px-4 font-semibold text-foreground w-40 whitespace-nowrap">
                              Topic Weight
                            </th>
                            <th className="text-center py-4 px-3 font-semibold text-foreground w-40 border-l border-border">
                              <DifficultyLevelInfo level="Knowledge">Knowledge</DifficultyLevelInfo>
                            </th>
                            <th className="text-center py-4 px-3 font-semibold text-foreground w-40 border-l border-border">
                              <DifficultyLevelInfo level="Understanding">Understanding</DifficultyLevelInfo>
                            </th>
                            <th className="text-center py-4 px-3 font-semibold text-foreground w-40 border-l border-r border-border">
                              <DifficultyLevelInfo level="Application+">Application+</DifficultyLevelInfo>
                            </th>
                            <th className="w-12"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {generatedBlueprint.topics.map((topic, index) => {
                            const topicTotal = topic.knowledge + topic.understanding + topic.higherOrderThinking;
                            const percentage = calculatePercentage(topic);
                            const isExpanded = expandedTopics.has(topic.id);

                            return (
                              <React.Fragment key={topic.id}>
                                <tr
                                  className={`border-b border-border hover-elevate align-top ${hasAttemptedNext && zeroItemTopicIds.has(topic.id) ? "bg-red-50" : ""}`}
                                  data-testid={`topic-row-${index}`}
                                >
                                  <td className="py-4 px-4">
                                    <div className="flex items-center gap-2">
                                      {/* Expand dropdown — only useful with the standards UI, which is
                                          mothballed. Hidden until STANDARDS_ENABLED is flipped back on. */}
                                      {STANDARDS_ENABLED && (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6"
                                          onClick={() => toggleTopicExpanded(topic.id)}
                                          data-testid={`button-expand-${index}`}
                                        >
                                          {isExpanded ? (
                                            <ChevronUp className="w-4 h-4" />
                                          ) : (
                                            <ChevronDown className="w-4 h-4" />
                                          )}
                                        </Button>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <AutoGrowTextarea
                                          value={topic.name}
                                          onChange={(e) => handleUpdateTopic(topic.id, "name", e.target.value)}
                                          maxLines={3}
                                          className={`w-full font-medium bg-muted hover:bg-muted/80 border-0 rounded-md px-3 py-1.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:bg-background transition-colors leading-snug ${hasAttemptedNext && unnamedTopicIds.has(topic.id) ? "ring-2 ring-red-500" : ""}`}
                                          data-testid={`input-topic-name-${index}`}
                                        />
                                        {topic.sourceAnchors && topic.sourceAnchors.length > 0 && (() => {
                                          const formatted = formatSourceAnchors(topic.sourceAnchors);
                                          if (!formatted) return null;
                                          return (
                                            <p
                                              className="mt-1 px-3 text-xs text-muted-foreground italic"
                                              title={formatted}
                                              data-testid={`text-source-anchors-${index}`}
                                            >
                                              {formatted}
                                            </p>
                                          );
                                        })()}
                                        {!(topic.sourceAnchors && topic.sourceAnchors.length > 0) && topic.addedManually && anyTopicHasSourceAnchors && (
                                          <p
                                            className="mt-1 px-3 text-xs text-muted-foreground italic"
                                            data-testid={`text-added-manually-${index}`}
                                          >
                                            Added by you
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="py-4 px-4 align-middle">
                                    <div className="flex items-center gap-2 whitespace-nowrap">
                                      <span className="text-primary font-semibold text-xl" data-testid={`text-percentage-${index}`}>
                                        {percentage}%
                                      </span>
                                      <span className="text-base text-muted-foreground">
                                        ({topicTotal} items)
                                      </span>
                                    </div>
                                  </td>
                                  {(() => {
                                    const hasStandards = (topic.standards || []).length > 0;
                                    const readOnlyTitle = hasStandards
                                      ? "Computed from per-standard distribution below — expand the row to edit."
                                      : undefined;
                                    return (
                                      <>
                                        <td className="relative p-0 border-l border-border">
                                          <ItemCountInput
                                            value={topic.knowledge}
                                            onCommit={(n) => Number(handleUpdateTopic(topic.id, "knowledge", n) ?? n)}
                                            stepper
                                            readOnly={hasStandards}
                                            disabled={hasStandards}
                                            title={readOnlyTitle}
                                            className={ITEM_INPUT_CLASS}
                                            data-testid={`input-knowledge-${index}`}
                                          />
                                        </td>
                                        <td className="relative p-0 border-l border-border">
                                          <ItemCountInput
                                            value={topic.understanding}
                                            onCommit={(n) => Number(handleUpdateTopic(topic.id, "understanding", n) ?? n)}
                                            stepper
                                            readOnly={hasStandards}
                                            disabled={hasStandards}
                                            title={readOnlyTitle}
                                            className={ITEM_INPUT_CLASS}
                                            data-testid={`input-understanding-${index}`}
                                          />
                                        </td>
                                        <td className="relative p-0 border-l border-r border-border">
                                          <ItemCountInput
                                            value={topic.higherOrderThinking}
                                            onCommit={(n) => Number(handleUpdateTopic(topic.id, "higherOrderThinking", n) ?? n)}
                                            stepper
                                            readOnly={hasStandards}
                                            disabled={hasStandards}
                                            title={readOnlyTitle}
                                            className={ITEM_INPUT_CLASS}
                                            data-testid={`input-higher-order-${index}`}
                                          />
                                        </td>
                                      </>
                                    );
                                  })()}
                                  <td className="py-4 px-2">
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="text-destructive hover:text-destructive"
                                          data-testid={`button-remove-${index}`}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Delete this topic?</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Topic "{topic.name?.trim() || "Untitled topic"}" will be removed from your blueprint.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() => handleRemoveTopic(topic.id)}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                          >
                                            Delete
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr key={`${topic.id}-details`} className="border-b border-border bg-muted/30">
                                    <td colSpan={6} className="py-4 px-6">
                                      {/* Full Topic Name (expanded view) */}
                                      <div className={STANDARDS_ENABLED ? "mb-4 pb-3 border-b border-border/50" : ""}>
                                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Full Topic Name</h4>
                                        <p className="text-lg font-semibold text-foreground" data-testid={`full-topic-name-${index}`}>
                                          {topic.name}
                                        </p>
                                      </div>

                                      {/* Standards UI — MOTHBALLED (STANDARDS_ENABLED=false). Kept for
                                          easy restore: per-standard taxonomic distribution + picker. */}
                                      {STANDARDS_ENABLED && (topic.standards || []).length > 0 && (
                                        <div className="mb-6 pb-6 border-b border-border/50">
                                          <h4 className="text-sm font-semibold mb-3">Per-Standard Taxonomic Distribution</h4>
                                          <p className="text-xs text-muted-foreground mb-4">
                                            Assign how many questions each standard should cover at each cognitive level. The topic's totals (in the row above) are the sum of these values.
                                          </p>
                                          <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                              <thead>
                                                <tr className="border-b border-border/50">
                                                  <th className="text-left font-medium text-muted-foreground py-2 pr-2">Standard</th>
                                                  <th className="text-center font-medium text-muted-foreground py-2 px-2 w-24">Knowledge</th>
                                                  <th className="text-center font-medium text-muted-foreground py-2 px-2 w-24">Understanding</th>
                                                  <th className="text-center font-medium text-muted-foreground py-2 px-2 w-28">Application+</th>
                                                  <th className="text-center font-medium text-muted-foreground py-2 pl-2 w-20">Subtotal</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {(topic.standards || []).map((std) => {
                                                  const subtotal = (std.knowledge || 0) + (std.understanding || 0) + (std.higherOrderThinking || 0);
                                                  return (
                                                    <tr key={std.code} className="border-b border-border/30">
                                                      <td className="py-2 pr-2">
                                                        <div className="font-medium text-foreground">{std.code}</div>
                                                        <div className="text-xs text-muted-foreground line-clamp-1" title={std.description}>{std.description}</div>
                                                      </td>
                                                      <td className="py-2 px-2">
                                                        <ItemCountInput
                                                          value={std.knowledge ?? 0}
                                                          onCommit={(n) => updateStandardCount(topic.id, std.code, 'knowledge', n) ?? n}
                                                          className="w-20 mx-auto text-center"
                                                          data-testid={`input-standard-knowledge-${std.code}`}
                                                        />
                                                      </td>
                                                      <td className="py-2 px-2">
                                                        <ItemCountInput
                                                          value={std.understanding ?? 0}
                                                          onCommit={(n) => updateStandardCount(topic.id, std.code, 'understanding', n) ?? n}
                                                          className="w-20 mx-auto text-center"
                                                          data-testid={`input-standard-understanding-${std.code}`}
                                                        />
                                                      </td>
                                                      <td className="py-2 px-2">
                                                        <ItemCountInput
                                                          value={std.higherOrderThinking ?? 0}
                                                          onCommit={(n) => updateStandardCount(topic.id, std.code, 'higherOrderThinking', n) ?? n}
                                                          className="w-20 mx-auto text-center"
                                                          data-testid={`input-standard-higher-order-${std.code}`}
                                                        />
                                                      </td>
                                                      <td className="py-2 pl-2 text-center font-medium">{subtotal}</td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      )}

                                      {STANDARDS_ENABLED && (
                                        <StandardsPicker
                                          topicName={topic.name}
                                          grade={readingLevelToGrade(config?.readingLevel)}
                                          selectedStandards={topic.standards || []}
                                          onChange={(standards) => handleStandardsChange(topic.id, standards)}
                                        />
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          {/* Add Topic (left) + total items as the final grid row */}
                          <tr className="border-t-2 border-border">
                            <td colSpan={2} className="py-4 px-4">
                              <Button
                                variant="outline"
                                onClick={handleAddTopic}
                                data-testid="button-add-topic"
                              >
                                Add Topic <Plus className="w-4 h-4" />
                              </Button>
                            </td>
                            <td colSpan={3} className="py-4 px-4 text-left border-l border-r border-border">
                              <span className="text-xl font-semibold text-primary" data-testid="text-total-items">
                                Total items: {calculateTotalItems()}
                              </span>
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                  </div>
                ) : generationError ? (
                  <div className="text-center py-12 max-w-xl mx-auto">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10 mb-4">
                      <AlertTriangle className="w-6 h-6 text-destructive" />
                    </div>
                    <p className="text-lg font-semibold text-foreground mb-1">
                      We couldn't generate a blueprint from your materials
                    </p>
                    <p className="text-sm text-muted-foreground mb-2">
                      Nothing usable came back, so we haven't filled in any topics. Check that your
                      uploaded materials contain readable content, then try again.
                    </p>
                    <p className="text-xs text-muted-foreground/80 mb-6 wrap-break-word">{generationError}</p>
                    <Button onClick={handleRegenerateBlueprint} data-testid="button-retry-blueprint">
                      Try again <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {onboardingBanner}
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">Generating blueprint...</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-between items-end">
              <Button variant="outline" onClick={handleBack} data-testid="button-back">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex flex-col items-end gap-1">
                {hasAttemptedNext && (hasZeroItems || hasUnnamed) && (
                  <p
                    className="text-sm text-red-600 max-w-xs text-right"
                    data-testid="text-blueprint-validation"
                  >
                    {hasZeroItems && (
                      <>Some topics have no items — add items or delete the topic.</>
                    )}
                    {hasZeroItems && hasUnnamed && <br />}
                    {hasUnnamed && (
                      <>Some topics still need a name.</>
                    )}
                  </p>
                )}
                <Button
                  onClick={handleNext}
                  disabled={!generatedBlueprint || generatedBlueprint.topics.length === 0}
                  data-testid="button-next"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* Wide view: assistant lives in the sidebar, sticky and full-
              height so it stays visible while the user scrolls the
              blueprint table. SmartAIChat switches to a flex layout that
              fills the wrapper when className includes "h-full". The
              chevron toggles between full-viewport and half-viewport
              height instead of collapsing to a bar. */}
          <div className="hidden xl:block">
            <div
              className={`sticky top-6 ${assistantHalfHeight ? "h-[70vh]" : "h-[calc(100vh-3rem)]"}`}
            >
              <SmartAIChat
                context="blueprint"
                blueprintContext={blueprintContext}
                materialsContext={materialsContext}
                className="h-full"
                toggleMode="half"
              />
            </div>
          </div>
        </div>

        {/* Narrow view: the sidebar assistant would otherwise stack below the
            whole table and get missed. Pin a compact assistant to the
            bottom-right instead — half width on small-to-medium screens, full
            width only when the viewport is really narrow. Starts collapsed
            (stout bar) so it's visible without covering the page. */}
        <div className="xl:hidden fixed bottom-0 right-0 z-50 w-full sm:w-1/2 max-w-xl">
          <SmartAIChat context="blueprint" blueprintContext={blueprintContext} materialsContext={materialsContext} defaultCollapsed showWidthToggle={false} />
        </div>
      </main>
    </div>
  );
}
